import React from 'react';
import { BookOpen, Calculator, Target, TrendingUp, Zap, Shield } from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'Comprendre avant d\'agir',
      description: 'Guides experts sur les statuts, fiscalité, aides et démarches. Tout est expliqué simplement.',
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Calculator,
      title: 'Simuler avant de décider',
      description: '15 simulateurs gratuits pour tester différents scénarios et faire les bons choix financiers.',
      color: 'from-blue-600 to-blue-800',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Target,
      title: 'Éviter les erreurs coûteuses',
      description: 'Conseils d\'experts pour éviter les pièges et optimiser votre situation dès le départ.',
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-50'
    },
    {
      icon: TrendingUp,
      title: 'Optimiser votre rentabilité',
      description: 'Techniques légales pour réduire vos charges et maximiser vos revenus d\'entrepreneur.',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50'
    },
    {
      icon: Zap,
      title: 'Données 2026 certifiées',
      description: 'Tous nos contenus et simulateurs incluent les dernières réformes fiscales votées.',
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-50'
    },
    {
      icon: Shield,
      title: 'Expertise juridique fiable',
      description: 'Contenus validés par des experts-comptables et juristes spécialisés en création d\'entreprise.',
      color: 'from-blue-700 to-blue-900',
      bgColor: 'bg-blue-50'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête de section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 mb-6">
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            <span className="text-sm font-medium text-blue-700">Pourquoi Bozzam Blog</span>
          </div>
          
          <h2 className="text-4xl md:text-6xl font-display font-black text-gray-900 mb-6">
            Votre succès entrepreneurial
            <br />
            <span className="gradient-text">commence ici</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Nous simplifions la création d'entreprise avec du contenu pédagogique premium et des outils pratiques ultra-précis
          </p>
        </div>

        {/* Grille des fonctionnalités */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div 
                key={index}
                className="group relative p-8 rounded-3xl bg-white border border-gray-100 hover:border-gray-200 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Icône avec gradient */}
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>

                {/* Contenu */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>

                {/* Effet de brillance au hover */}
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-transparent via-white/5 to-transparent transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Section stats en bas */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-4 sm:gap-8 px-4 sm:px-8 py-4 sm:py-6 rounded-2xl glass border border-white/20 backdrop-blur-lg">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-gray-900">INSEE</div>
              <div className="text-xs sm:text-sm text-gray-600">Sources officielles</div>
            </div>
            <div className="w-px h-8 sm:h-12 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-gray-900">2026</div>
              <div className="text-xs sm:text-sm text-gray-600">Données certifiées</div>
            </div>
            <div className="w-px h-8 sm:h-12 bg-gray-200"></div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold text-gray-900">100%</div>
              <div className="text-xs sm:text-sm text-gray-600">Gratuit & sans pub</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;